<template>
  <div class="text-primaryDark relative pb-20">
    <top-nav-bar />
    <transition name="fade">
      <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-80">
        <loader class="absolute center-h-v" />
      </div>
    </transition>
    <transition name="fade">
      <div
        v-if="success"
        class="bg-black fixed bg-opacity-80 inset-0 z-50 h-screen w-full"
      >
        <div class="fixed center-h-v bg-white shadow-2xl sm:w-1/4 w-3/4 p-5">
          <img
            class="h-14 mx-auto object-contain"
            src="@/assets/icons/check.png"
          />
          <p class="text-center text-sm font-semibold py-5">
            Your password has been changed successfully. Please login again.
          </p>

          <button
            @click="logOut()"
            class="bg-primaryDark w-full text-white font-bold py-2 px-4 rounded"
          >
            Login
          </button>
        </div>
      </div>
    </transition>
    <div class="flex justify-center mt-10">
      <div class="sm:w-1/2 w-full">
        <img
          class="h-16 mx-auto object-contain"
          src="@/assets/icons/lock.png"
        />
        <p class="text-center py-3 text-2xl font-semibold">
          Change your password
        </p>
        <p class="text-center pb-3 text-sm">
          In order to protect your account, make sure the password:
        </p>
        <div class="flex justify-center">
          <ul class="list-disc text-sm font-bold">
            <li>Has an upper and lower case letter.</li>
            <li>Contains a special character.</li>
            <li>Is at least 6 characters long.</li>
            <li>Has a number.</li>
          </ul>
        </div>
        <form @submit.prevent="changePassword">
          <div class="flex flex-col gap-3 justify-center w-1/2 mx-auto mt-3">
            <div>
              <label class="text-xs">Old Password</label>
              <input
                v-model="data.OldPassword"
                required
                type="password"
                class="p-1 mt-0.5 block w-full bg-gray-200 focus:bg-white"
              />
              <span
                v-if="modelError && modelError['model.' + 'OldPassword']"
                class="text-red-500 text-sm block w-full"
              >
                {{ modelError["model." + "OldPassword"][0] }}
              </span>
            </div>
            <div>
              <label class="text-xs">New Password</label>
              <input
                v-model="data.NewPassword"
                required
                type="password"
                class="p-1 mt-0.5 block w-full bg-gray-200 focus:bg-white"
              />
              <span
                v-if="modelError && modelError['model.' + 'NewPassword']"
                class="text-red-500 text-sm block w-full"
              >
                {{ modelError["model." + "NewPassword"][0] }}
              </span>
            </div>
            <div>
              <label class="text-xs">Confirm Password</label>
              <input
                v-model="data.ConfirmPassword"
                required
                type="password"
                class="p-1 mt-0.5 block w-full bg-gray-200 focus:bg-white"
              />
              <span
                v-if="modelError && modelError['model.' + 'ConfirmPassword']"
                class="text-red-500 text-sm block w-full"
              >
                {{ modelError["model." + "ConfirmPassword"][0] }}
              </span>
            </div>
          </div>
          <p v-if="errorMessage" class="text-red-500 text-sm text-center mt-3">
            {{ errorMessage }}
          </p>
          <div class="w-full flex justify-center mt-8">
            <button
              class="
                text-sm
                bg-primaryDark
                hover:bg-buttonColor
                text-white
                sm:w-1/3
                w-1/2
                font-bold
                py-2
                px-4
                rounded
              "
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import TopNavBar from "../components/dashboardLayout/topNavBar.vue";
import Loader from "../components/loader/loader.vue";
import axios from "axios";
import { mapGetters } from "vuex";
export default {
  name: "ChangePassword",
  components: {
    TopNavBar,
    Loader,
  },
  metaInfo: {
    title: "LipaLink | Change password",
  },
  data() {
    return {
      loading: false,
      success: false,
      modelError: {},
      errorMessage: null,
      data: {
        OldPassword: null,
        NewPassword: null,
        ConfirmPassword: null,
      },
    };
  },
  computed: {
    ...mapGetters(["getToken", "getOrganization"]),
  },
  methods: {
    logOut() {
      const org = this.getOrganization;
      this.$store.commit("logout");
      if (org) {
        this.$store.commit("setOrganization", org);
      }
      this.$router.replace({ name: "login" });
    },
    changePassword() {
      this.loading = true;
      this.success = false;
      this.errorMessage = null;
      this.modelError = {};

      const org = this.getOrganization;
      if (!org) {
        this.loading = false;
        this.errorMessage =
          "Organization is not set. Please login again or access the app via your organization link.";
        return;
      }

      const token = this.getToken;
      if (!token) {
        this.loading = false;
        this.errorMessage =
          "No access token found. Please log in again and try changing your password.";
        return;
      }

      const options = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          Accept: "text/plain",
        },
      };

      const payload = {
        oldPassword: this.data.OldPassword,
        newPassword: this.data.NewPassword,
        confirmPassword: this.data.ConfirmPassword,
      };

      axios
        .post(
          `account/${encodeURIComponent(org)}/change-password`,
          payload,
          options
        )
        .then(() => {
          this.loading = false;
          this.success = true;
          // Record that the password is now changed so the dashboard gate
          // stops redirecting, then proceed into the app.
          this.$store.commit("setPasswordChanged", true);
          this.$router.replace({
            name: "dashboard",
            params: { organization: org },
          });
        })
        .catch((error) => {
          this.loading = false;
          const data = error?.response?.data;
          const status = error?.response?.status;

          const apiError = (Array.isArray(data?.Errors) ? data.Errors.join(', ') : data?.Errors) || data?.Message;

          if (status === 401) {
            this.errorMessage = apiError || "Your session may have expired. Please log in again and try again.";
            return;
          }

          this.errorMessage = apiError || error?.message || "Failed to change password.";
        });
    },
  },
};
</script>